import React, { createContext, useContext, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const EXPIRY_MS = 48 * 60 * 60 * 1000;

export interface PendingJobData {
  jobId: string;
  gameName: string;
  gameType: string;
  resultHtml: string;
  requestPayload: Record<string, unknown>;
}

interface PendingJobContextValue {
  pendingJob: PendingJobData | null;
  jobExpired: boolean;
  loadJob: (jobId: string, gameName: string) => Promise<void>;
  clearPendingJob: () => void;
  clearJobExpired: () => void;
  isLoadingJob: boolean;
}

const PendingJobContext = createContext<PendingJobContextValue | undefined>(undefined);

export const PendingJobProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pendingJob, setPendingJob] = useState<PendingJobData | null>(null);
  const [jobExpired, setJobExpired] = useState(false);
  const [isLoadingJob, setIsLoadingJob] = useState(false);

  const loadJob = useCallback(async (jobId: string, gameName: string) => {
    setIsLoadingJob(true);
    try {
      const { data, error } = await supabase
        .from('ai_generation_jobs')
        .select('id, game_type, game_name, result_html, request_payload, status, completed_at, images_persisted')
        .eq('id', jobId)
        .maybeSingle();

      if (error || !data || data.status !== 'completed' || !data.result_html) {
        return;
      }

      if (data.completed_at && !data.images_persisted) {
        const completedTime = new Date(data.completed_at).getTime();
        const age = Date.now() - completedTime;
        if (age > EXPIRY_MS) {
          setJobExpired(true);
          return;
        }
      }

      setPendingJob({
        jobId: data.id,
        gameName: data.game_name || gameName,
        gameType: data.game_type,
        resultHtml: data.result_html,
        requestPayload: (data.request_payload as Record<string, unknown>) || {},
      });
    } finally {
      setIsLoadingJob(false);
    }
  }, []);

  const clearPendingJob = useCallback(() => {
    setPendingJob(null);
  }, []);

  const clearJobExpired = useCallback(() => {
    setJobExpired(false);
  }, []);

  return (
    <PendingJobContext.Provider value={{ pendingJob, jobExpired, loadJob, clearPendingJob, clearJobExpired, isLoadingJob }}>
      {children}
    </PendingJobContext.Provider>
  );
};

export const usePendingJob = (): PendingJobContextValue => {
  const ctx = useContext(PendingJobContext);
  if (!ctx) throw new Error('usePendingJob must be used within PendingJobProvider');
  return ctx;
};
